import { getAuthValues, getSSOAuthValues, setAuthStage } from '../composables/auth';
import { setUrlStage } from '../composables/url';

export class AuthService {
    private static instance: AuthService | null = null;

    private constructor() {}

    public static getInstance(): AuthService {
        // Check for STAGE_NAME environment variable first, fallback to MODE
        const stage = import.meta.env.MODE;
        setAuthStage(stage);
        setUrlStage(stage);
        if (AuthService.instance === null) {
            AuthService.instance = new AuthService();
        }

        return AuthService.instance;
    }

    private generateRandomString(length: number): string {
        const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~';
        const charactersLength = characters.length;
        const randomValues = new Uint8Array(length);

        window.crypto.getRandomValues(randomValues);

        return Array.from(randomValues)
            .map((value) => characters.charAt(value % charactersLength))
            .join('');
    }

    private async generatePkceChallenge(codeVerifier: string): Promise<string> {
        // Encode the code verifier as a Uint8Array
        const encoder = new TextEncoder();
        const data = encoder.encode(codeVerifier);

        // Generate the SHA-256 hash
        const hashBuffer = await window.crypto.subtle.digest('SHA-256', data);

        // Convert the hash to a base64 URL-safe string
        const hashArray = Array.from(new Uint8Array(hashBuffer));
        const base64String = btoa(String.fromCharCode(...hashArray))
            .replace(/\+/g, '-')
            .replace(/\//g, '_')
            .replace(/=+$/, ''); // Remove trailing '='

        return base64String;
    }
    async login() {}

    /**
     * Converts a Uint8Array to a Base64 URL-safe string.
     * @param buffer The input buffer.
     * @returns {string} A Base64 URL-safe encoded string.
     */
    private base64UrlEncode(buffer: Uint8Array): string {
        return btoa(String.fromCharCode(...buffer))
            .replace(/\+/g, '-') // Replace '+' with '-'
            .replace(/\//g, '_') // Replace '/' with '_'
            .replace(/=+$/, ''); // Remove padding '='
    }

    /**
     * Generates a SHA-256 hash of the input string.
     * @param input The string to hash.
     * @returns {Promise<string>} A Base64 URL-safe encoded SHA-256 hash.
     */
    async generateCodeChallenge(codeVerifier: string): Promise<string> {
        const encoder = new TextEncoder();
        const data = encoder.encode(codeVerifier);
        const hashBuffer = await crypto.subtle.digest('SHA-256', data);
        return this.base64UrlEncode(new Uint8Array(hashBuffer));
    }

    async getOAuthUrl(): Promise<string> {
        const codeVerifier = this.generateRandomString(43);
        const codeChallenge = await this.generatePkceChallenge(codeVerifier);

        window.localStorage.setItem('code_verifier', codeVerifier);

        // return this.getAuthRedirectURL();

        return this.getAuthUrl(codeChallenge);
    }

    private getAuthUrl(challenge: string): string {
        const path = `/oauth2/${getAuthValues().serverId}/v1/authorize`;
        const host = 'okta.mit.edu';
        const newUri = new URL(`https://${host}${path}`);

        newUri.searchParams.append('response_type', 'code');
        newUri.searchParams.append('client_id', getAuthValues().clientId);
        newUri.searchParams.append('scope', getAuthValues().scopes);
        newUri.searchParams.append('redirect_uri', getAuthValues().redirectUri);
        newUri.searchParams.append('code_challenge', challenge);
        newUri.searchParams.append('code_challenge_method', 'S256');
        newUri.searchParams.append('state', '1');

        return newUri.toString();
    }

    async hasValidToken(): Promise<boolean> {
        try {
            await this.getAccessToken();
            return true;
        } catch (error) {
            return false;
        }
    }

    async fetchToken(url: string): Promise<void> {
        const queryParameters = new URLSearchParams(url.split('?')[1]);
        const code = queryParameters.get('code');

        if (code === null) {
            throw new Error('No code found in URL');
        }

        const codeVerifier = window.localStorage.getItem('code_verifier');
        if (!codeVerifier) {
            throw new Error('Code verifier not found in localStorage');
        }

        const body = new URLSearchParams({
            code: code,
            redirect_uri: getAuthValues().redirectUri,
            code_verifier: codeVerifier,
            client_id: getAuthValues().clientId,
            grant_type: 'authorization_code'
        });

        await this.getToken(body.toString());
    }

    private async getToken(body: string): Promise<void> {
        const targetUrl = `https://okta.mit.edu/oauth2/${getAuthValues().serverId}/v1/token`;
        const response: Response = await fetch(targetUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded'
            },
            body: body
        });

        if (!response.ok) {
            throw new Error('Failed to fetch token');
        }

        const data = await response.json();

        // Store the token in localStorage
        window.localStorage.setItem('idToken', data.id_token);
        window.localStorage.setItem('accessToken', data.access_token);
        window.localStorage.setItem('refreshToken', data.refresh_token);
        window.localStorage.setItem('tokenType', data.token_type);
        window.localStorage.setItem('expiresIn', data.expires_in);
    }

    async getAccessToken(forceRefresh: boolean = false): Promise<string> {
        let token = window.localStorage.getItem('accessToken');

        if (token === null) {
            throw new Error('No token found in localStorage');
        }

        const exp = this.decodeJwt(token);

        if (exp == null) {
            throw new Error('No expiration time found in token');
        }

        if (Number(exp) < Date.now() / 1000 || forceRefresh) {
            token = await this.refreshToken();
        }

        return token;
    }

    async refreshToken(): Promise<string> {
        const refreshToken = window.localStorage.getItem('refreshToken');

        if (refreshToken === null) {
            throw new Error('No refresh token found in localStorage');
        }

        const body = new URLSearchParams({
            refresh_token: refreshToken,
            redirect_uri: getAuthValues().redirectUri,
            client_id: getAuthValues().clientId,
            grant_type: 'refresh_token'
        });

        await this.getToken(body.toString());

        return window.localStorage.getItem('accessToken') as string;
    }

    private decodeJwt(token: string): string {
        try {
            // Split the token into parts
            const [, payload] = token.split('.');

            // Decode the Base64Url-encoded payload
            const decodedPayload = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));

            // Extract and return the `exp` field
            return decodedPayload.exp;
        } catch (error) {
            console.error('Failed to decode JWT:', error);
            throw new Error('Failed to decode JWT');
        }
    }
}
